import React, { useEffect, useState } from 'react';
import './HouseAdminConfig.scss';

// Mirrors doorLock.ts's LOCK_LEVELS - the native ObjectReference.setLockLevel
// Papyrus function's own 0-100 scale (Novice/Apprentice/Adept/Expert/Master
// in 25-unit steps). Same labels/values as DoorLockConfig's own
// LOCK_LEVEL_OPTIONS - kept in sync by hand since there's no shared front
// constants module, but never invent a different scale here.
const LOCK_LEVEL_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 0, label: 'Très facile' },
  { value: 25, label: 'Facile' },
  { value: 50, label: 'Moyen' },
  { value: 75, label: 'Difficile' },
  { value: 100, label: 'Très difficile' },
];
const DEFAULT_LOCK_LEVEL = 50;

interface ShowEventDetail {
  refrId: number;
  // admin: every field; sale: price only; rent: weekly rent only.
  mode?: 'admin' | 'sale' | 'rent';
  // commerce: the rent form of a shop (own title and wording), with its minimum weekly rent
  kind?: 'commerce' | null;
  minRentPerWeek?: number | null;
  currentPrice: number | null;
  currentHoldId?: string | null;
  currentRentPricePerWeek: number | null;
  maxMembersOverride: number | null;
  defaultMaxMembers: number | null;
  lockLevel: number | null;
  displayNameOverride: string | null;
  currentDisplayName: string | null;
}

const send = (payload: Record<string, unknown>): void => {
  if ((window as any).skyrimPlatform?.sendMessage) {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify({ source: 'house-admin-config', ...payload }));
  }
};

const HouseAdminConfig: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [refrId, setRefrId] = useState<number | null>(null);
  const [mode, setMode] = useState<'admin' | 'sale' | 'rent'>('admin');
  const [priceDraft, setPriceDraft] = useState('500');
  const [holdIdDraft, setHoldIdDraft] = useState('');
  const [rentPriceDraft, setRentPriceDraft] = useState('');
  const [maxMembersDraft, setMaxMembersDraft] = useState('');
  const [defaultMaxMembers, setDefaultMaxMembers] = useState<number | null>(null);
  const [lockpickable, setLockpickable] = useState(false);
  const [lockLevelDraft, setLockLevelDraft] = useState<number>(DEFAULT_LOCK_LEVEL);
  const [displayNameDraft, setDisplayNameDraft] = useState('');
  const [currentDisplayName, setCurrentDisplayName] = useState('');
  // Rent listings only: a player who moves in right away (empty = listed for anyone)
  const [occupantDraft, setOccupantDraft] = useState('');
  const [commerce, setCommerce] = useState(false);
  const [minRent, setMinRent] = useState(0);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as ShowEventDetail;
      setRefrId(detail.refrId);
      setMode(detail.mode ?? 'admin');
      setPriceDraft(String(detail.currentPrice ?? 500));
      setHoldIdDraft(detail.currentHoldId ?? '');
      setRentPriceDraft(detail.currentRentPricePerWeek ? String(detail.currentRentPricePerWeek) : '');
      // Left BLANK (not pre-filled with defaultMaxMembers/currentDisplayName)
      // when no override is actually configured - shown only as a
      // placeholder below. Pre-filling the real draft with a merely
      // COMPUTED fallback would silently turn "admin only touched the
      // price field" into "admin just froze this house's member cap/name at
      // today's value forever" the moment they hit submit, since this form
      // always sends whatever's currently in the field.
      setMaxMembersDraft(detail.maxMembersOverride !== null ? String(detail.maxMembersOverride) : '');
      setDefaultMaxMembers(detail.defaultMaxMembers ?? null);
      setLockpickable(detail.lockLevel !== null && detail.lockLevel !== undefined);
      setLockLevelDraft(detail.lockLevel ?? DEFAULT_LOCK_LEVEL);
      setDisplayNameDraft(detail.displayNameOverride ?? '');
      setCurrentDisplayName(detail.currentDisplayName ?? '');
      setOccupantDraft('');
      setCommerce(detail.kind === 'commerce');
      setMinRent(detail.minRentPerWeek ?? 0);
      setVisible(true);
    };
    window.addEventListener('skymp5-client:showHouseAdminConfig', handler);
    return () => window.removeEventListener('skymp5-client:showHouseAdminConfig', handler);
  }, []);

  const close = () => {
    setVisible(false);
    send({ action: 'close' });
  };

  const submit = () => {
    if (refrId === null) return;
    const price = mode === 'rent' ? 1 : Number(priceDraft);
    if (!Number.isFinite(price) || price <= 0) return;

    const rentPricePerWeek = Number(rentPriceDraft);
    if (mode === 'rent' && !(rentPricePerWeek > 0)) return;
    if (commerce && rentPricePerWeek < minRent) return;
    const wantsRent = mode !== 'sale' && Number.isFinite(rentPricePerWeek) && rentPricePerWeek > 0;
    const maxMembersOverride = Number(maxMembersDraft);

    send({
      action: 'submit',
      refrId,
      price,
      // Which listing a non-admin is making; admins send the whole form.
      listing: mode === 'admin' ? undefined : commerce ? 'commerce' : mode,
      holdId: holdIdDraft.trim(),
      rentPricePerWeek: wantsRent ? rentPricePerWeek : '',
      maxMembersOverride: Number.isFinite(maxMembersOverride) && maxMembersOverride > 0 ? maxMembersOverride : '',
      lockLevel: lockpickable ? lockLevelDraft : '',
      displayNameOverride: displayNameDraft.trim(),
      occupantName: mode === 'rent' ? occupantDraft.trim() : '',
    });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="house-admin">
      <div className="house-admin__window skyrim-panel">
        <div className="house-admin__header">
          <span className="house-admin__title">{commerce ? 'Commerce en location' : mode === 'rent' ? 'Logement en location' : 'Logement en vente'}</span>
        </div>

        <div className="house-admin__body">
          {mode !== 'rent' && (
            <>
              <label className="house-admin__label">Prix d'achat (Septims)</label>
              <input
                className="house-admin__input"
                type="number"
                min="1"
                value={priceDraft}
                onChange={(e) => setPriceDraft(e.target.value)}
                autoFocus
              />
            </>
          )}

          {mode !== 'sale' && (
            <>
              <label className="house-admin__label">
                {mode === 'rent'
                  ? `Loyer hebdomadaire (Septims${commerce && minRent > 0 ? `, ${minRent} minimum` : ''})`
                  : 'Loyer hebdomadaire (Septims, vide = pas de location)'}
              </label>
              <input
                className="house-admin__input"
                type="number"
                min={commerce && minRent > 0 ? String(minRent) : '0'}
                placeholder={commerce && minRent > 0 ? String(minRent) : 'Aucun'}
                value={rentPriceDraft}
                onChange={(e) => setRentPriceDraft(e.target.value)}
              />
            </>
          )}

          {mode === 'rent' && (
            <>
              <label className="house-admin__label">Occupant (nom du joueur connecté, vide = libre à la location)</label>
              <input
                className="house-admin__input"
                type="text"
                placeholder={commerce ? 'Aucun : le commerce reste à louer' : 'Aucun : le logement reste à louer'}
                value={occupantDraft}
                onChange={(e) => setOccupantDraft(e.target.value.slice(0, 60))}
              />
            </>
          )}

          {mode === 'admin' && (
            <>
              <label className="house-admin__label">ID du hold (vide = vente libre)</label>
              <input
                className="house-admin__input"
                type="text"
                placeholder="ex: blancherive"
                value={holdIdDraft}
                onChange={(e) => setHoldIdDraft(e.target.value)}
              />
            </>
          )}

          {mode === 'admin' && (
            <>
          <label className="house-admin__label">
            Nombre de clés maximum (vide = défaut{defaultMaxMembers !== null ? `, ${defaultMaxMembers}` : ''})
          </label>
          <input
            className="house-admin__input"
            type="number"
            min="0"
            placeholder={defaultMaxMembers !== null ? String(defaultMaxMembers) : 'Défaut'}
            value={maxMembersDraft}
            onChange={(e) => setMaxMembersDraft(e.target.value)}
          />

          <label className="house-admin__label">
            Nom affiché (vide = généré automatiquement{currentDisplayName ? `, actuellement "${currentDisplayName}"` : ''})
          </label>
          <input
            className="house-admin__input"
            type="text"
            placeholder={currentDisplayName || 'Nom automatique'}
            value={displayNameDraft}
            onChange={(e) => setDisplayNameDraft(e.target.value.slice(0, 60))}
          />

          <label className="house-admin__toggle">
            <input
              type="checkbox"
              checked={lockpickable}
              onChange={(e) => setLockpickable(e.target.checked)}
            />
            <span className="house-admin__toggle-box" />
            Porte crochetable
          </label>

          {lockpickable && (
            <>
              <label className="house-admin__label">Difficulté du crochetage</label>
              <select
                className="house-admin__select"
                value={lockLevelDraft}
                onChange={(e) => setLockLevelDraft(Number(e.target.value))}
              >
                {LOCK_LEVEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </>
          )}
            </>
          )}
        </div>

        <div className="house-admin__footer">
          <button type="button" className="house-admin__button house-admin__button--secondary" onClick={close}>
            Annuler
          </button>
          <button type="button" className="house-admin__button" onClick={submit}>
            {mode === 'rent'
              ? (occupantDraft.trim() ? (commerce ? 'Attribuer le commerce' : 'Attribuer le logement') : 'Mettre en location')
              : 'Mettre en vente'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default HouseAdminConfig;
